import { useState } from 'react'
import { Building2 } from 'lucide-react'
import { Button, Card, ErrorState, LoadingState, PageHeader, toast, useResource } from '@retink/web-ui'
import { api, switchTenant } from '../lib/api'

interface TenantChoice {
  id: string
  name: string
  slug: string
  region: string | null
  orgId: string
  orgName: string
}

export function ChooseTenant() {
  const [switching, setSwitching] = useState<string | null>(null)
  const { data: tenants = [], loading, error, reload } = useResource<TenantChoice[]>(
    () => api.get('/api/me/tenants'),
    [],
  )

  async function choose(tenant: TenantChoice) {
    setSwitching(tenant.id)
    try {
      await switchTenant(tenant.id, '/dashboard')
    } catch {
      setSwitching(null)
      toast.error('Could not open that tenant')
    }
  }

  return (
    <main className="min-h-full bg-background px-5 py-10 text-foreground">
      <div className="mx-auto max-w-3xl">
        <PageHeader title="Choose a tenant" description="Your account can access more than one tenant." />
        {loading && <Card className="p-8"><LoadingState label="Loading tenants…" /></Card>}
        {!loading && error && <Card className="p-8"><ErrorState message={error} onRetry={reload} /></Card>}
        {!loading && !error && tenants.length === 0 && (
          <Card className="p-8 text-sm text-muted-foreground">Your account has no active tenant.</Card>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {tenants.map((tenant) => (
            <Card key={tenant.id} className="flex items-center gap-4 p-4">
              <div className="rounded-lg bg-muted p-2 text-[var(--surface-tenant)]"><Building2 /></div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{tenant.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {tenant.orgName}{tenant.region ? ` · ${tenant.region}` : ''}
                </div>
              </div>
              <Button onClick={() => void choose(tenant)} disabled={switching !== null}>
                {switching === tenant.id ? 'Opening…' : 'Open'}
              </Button>
            </Card>
          ))}
        </div>
      </div>
    </main>
  )
}
